import moment from 'moment';
import { NumericFormat } from 'react-number-format'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons';
import { FunctionComponent, SetStateAction } from 'react';
import { TableDisplay } from '../../miscellaneous-components/table-display';
import { EditUploadLineDirectEmissionDialog } from '../../../dialogs/settings/data-import/edit-upload-line-direct-emission-dialog';
import { IEditFileUploadLineDirectEmission, IFileUploadLineDirectEmission } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { useNavigate } from 'react-router-dom';
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked';
import { Chip, Tooltip, IconButton, Stack } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';

export const CsvUploadDirectEmissions: FunctionComponent<{
  uploadId: string;
  fileUploadLineDirectEmissions: IFileUploadLineDirectEmission[],
  editClickHandler: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    uploadLine: IFileUploadLineDirectEmission) => void,
  deleteHandler: (id: string) => void,
  openDialog: boolean,
  setValidatedUploadLines: React.Dispatch<SetStateAction<any>>,
  selected: string[],
  setSelected: React.Dispatch<SetStateAction<any>>,
  editUploadLine: IFileUploadLineDirectEmission | undefined,
  closeDialogHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineDirectEmission) => void,
  status: string,
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  setOrder: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
  isSubmitting: boolean,
  selectAllFiltered?: boolean,
  onExitSelectAllFiltered?: (remainingIdsOnPage: string[]) => void,
}> = ({
  uploadId,
  fileUploadLineDirectEmissions,
  editClickHandler,
  openDialog,
  setValidatedUploadLines,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setPerPage,
  selected,
  setSelected,
  editUploadLine,
  closeDialogHandler,
  submitHandler,
  deleteHandler,
  setOrder,
  setOrderBy,
  status,
  isSubmitting,
  selectAllFiltered,
  onExitSelectAllFiltered, }) => {
    const navigate = useNavigate()
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()

    const mapValidationErrors = (uploadLines: any[]) => {
      return uploadLines.map((uploadLine: any) => {
        const rowNumber = typeof uploadLine.rowNumber === 'number' ? uploadLine.rowNumber : undefined
        const validationErrors = Array.isArray(uploadLine.validationErrors) ? uploadLine.validationErrors : []

        const fieldList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <div key={idx}>{ve.field || 'Unknown'}</div>
            ))}
          </Stack>
        ) : ''

        const errorList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => {
              const errorText = ve.message || 'Validation failed'
              const truncatedError = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText
              return <div key={idx}>{truncatedError}</div>
            })}
          </Stack>
        ) : ''

        const severityList = validationErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {validationErrors.map((ve: any, idx: number) => (
              <Chip
                key={idx}
                color={ve.severity === 'warning' ? 'warning' : 'error'}
                label={ve.severity === 'warning' ? 'Warning' : 'Error'}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        ) : status === 'pending' ? (
          <Chip
            color={uploadLine.commitStatus === 'FAILED' ? 'error' : 'success'}
            label={uploadLine.commitStatus === 'FAILED' ? 'Failed' : 'Ready'}
            size="small"
            variant="outlined"
          />
        ) : ''

        return {
          id: uploadLine.id,
          row: rowNumber || '',
          field: fieldList,
          error: errorList,
          severity: severityList,
          action: (
            <Tooltip title="Edit" placement='top'>
              <IconButton
                onClick={() => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
                size="small">
                <EditOutlined sx={{ cursor: 'pointer', fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ),
        }
      })
    }

    const headCells: IHeadCell[] = status === 'pending' ?
      [
        { id: 'action', numeric: false, label: 'Action', sortable: false },
        { id: 'row', numeric: true, label: 'Row', sortable: false },
        { id: 'field', numeric: false, label: 'Field', sortable: false },
        { id: 'error', numeric: false, label: 'Error', sortable: false },
        { id: 'severity', numeric: false, label: 'Severity', sortable: false },
      ]
      :
      [
        ...((status !== 'discarded' && ((status === 'completed') || ((status === 'pending' || status === 'validated') && !isReportPeriodLocked))) ? [{ id: 'action', numeric: false, label: 'Action', sortable: false }] : []),
        { id: 'groupName', numeric: false, label: 'Group', sortable: true },
        { id: 'categoryName', numeric: false, label: 'Category', sortable: true },
        { id: 'unitsConsumed', numeric: true, label: 'Units Consumed (kg CO2e)', sortable: true },
        { id: 'startDate', numeric: false, label: 'Start Date', sortable: true },
        { id: 'endDate', numeric: false, label: 'End Date', sortable: true },
        { id: 'dataType', numeric: true, label: 'Data Type', sortable: true },
      ];

    const rows = status === 'pending'
      ? mapValidationErrors(fileUploadLineDirectEmissions || [])
      : fileUploadLineDirectEmissions?.map((uploadLine: IFileUploadLineDirectEmission) => {
        return {
          id: uploadLine.id,
          groupName: uploadLine.groupName,
          categoryName: uploadLine.categoryName,
          unitsConsumed: (<NumericFormat value={uploadLine.unitsConsumed} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale />),
          dataType: uploadLine.dataType,
          startDate: moment(uploadLine.startDate).format('DD/MM/YYYY'),
          endDate: moment(uploadLine.endDate).format('DD/MM/YYYY'),
          action: status === 'validated' && !isReportPeriodLocked ? (
            <TableActionButtons
              id={uploadLine.id}
              includeEdit={true}
              includeDelete={true}
              onEdit={(_id: string) => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
              onDelete={(id: string) => deleteHandler(id)}
            />
          ) : status === 'completed' ? (
            <TableActionButtons
              id={uploadLine.id}
              includeView={true}
              onView={() => uploadLine.emissionId ? viewTransactionClickHandler(uploadLine.emissionId) : undefined}
              disableButton={!uploadLine.emissionId}
            />
          ) : null
        }
      })

    const viewTransactionClickHandler = (transactionId: string) => {
      navigate(`/emissions/overview/${transactionId}?backLink=${encodeURIComponent(`/settings/data-upload/import-history/emissions/${uploadId}?tabId=1`)}&backText=${encodeURIComponent('Back to import')}`)
    }

    const handleSelected = (selectedRowId: string[]) => {
      const formatted = selectedRowId.map(row => {
        return {
          id: row
        }
      })
      setValidatedUploadLines(formatted)
    }

    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
      setOrderBy(orderBy)
      setOrder(order)
    };

    return (
      <>
        <TableDisplay
          onOrderChange={handleOrderChange}
          headCells={headCells}
          rows={rows}
          selectByIcon={(status === 'validated' || status === 'pending') && !isReportPeriodLocked ? false : true}
          selectByCheckboxOnly={((status === 'validated' || status === 'pending') && !isReportPeriodLocked) ? true : false}
          handleSelected={handleSelected}
          selected={selected}
          setSelected={setSelected}
          selectAllFiltered={selectAllFiltered}
          onExitSelectAllFiltered={onExitSelectAllFiltered}
          inputPage={inputPage}
          haveCheckboxSelect={((status === 'validated' || status === 'pending') && !isReportPeriodLocked) ? true : false}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />
        {editUploadLine && openDialog && !isReportPeriodLocked &&
          <EditUploadLineDirectEmissionDialog
            isSubmitting={isSubmitting}
            open={openDialog}
            closeHandler={closeDialogHandler}
            submitHandler={submitHandler}
            editUploadLine={editUploadLine} />}
      </>
    )
  }